<template>
  <sales-charges-module v-if="chargesNewLayoutEnabled" />
  <div v-else>
    <div
      v-if="loadingDetail"
      class="eu-pa_6 eu-align_center"
    >
      <emd-loader :type="'_invert'"></emd-loader>
    </div>

    <div
      v-else
      class="el-stripe"
    >
      <alert-reason-refusal :data="charge.last_transaction" />
      <br />
      <emd-alert
        v-if="alertSuccess.isOpen"
        variant="success"
        :title="alertSuccess.title"
        @cancel="alertSuccess.isOpen = false"
      >
        <template slot="content">
          {{ alertSuccess.message }}
        </template>
      </emd-alert>

      <emd-alert
        v-if="isCancelableBoletoSuccess"
        variant="success"
        :title="$t('charge.detail.boleto-cancel.alert.success.title')"
        :autoclose="false"
      >
        <template slot="content">
          {{ this.$t('charge.detail.boleto-cancel.alert.success.content') }}
          <a
            target="_blank"
            class="emd-link underline"
            rel="noopener"
            href="https://pagarme.helpjuice.com/p1-transa%C3%A7%C3%B5es-e-estornos/estorno-prazos-de-estorno-total-e-parcial?from_search=85003811"
            >{{
              $t('charge.detail.boleto-cancel.alert.success.content-link')
            }}</a
          >
        </template>
      </emd-alert>

      <charge-detail-table
        ref="chargeDetailTable"
        :charge="charge"
        :isCancelableBoleto="isCancelableBoleto"
        :canRetryCharge="canRetryCharge"
        :showAttemptButton="showAttemptButton"
        @resync="resync"
        @getLetter="getLetter"
        @openModalRefund="openModalRefund"
        @openRetryModal="openRetryModal"
        @openCaptureModal="openCaptureModal"
        @openConfirmModal="openConfirmModal"
        @openAttemptModal="openAttemptModal"
        @openModalSimulatorPayments="openModalSimulatorPayments"
      />

      <recipients
        :splits="getSplits"
        :consolidated="getConsolidated"
        v-if="showPayableInstallment"
      />

      <div class="eu-flex eu-justify_space-between">
        <div class="eu-flex-item eu-basis_12 eu-basis_6_lg eu-pr_3">
          <!-- Resumo do recebimento -->
          <transaction-summary
            :charge="charge"
            :isBoleto="isBoleto"
            :isPSP="isPSP"
          />
          <!-- Resumo do recebimento -->

          <payment-method
            v-if="charge.last_transaction"
            :charge="charge"
            :merchantId="merchantId"
            :accountId="accountId"
          />

          <payer-details
            v-if="hasPayerPix"
            :payer="charge.last_transaction.payer"
          />
        </div>
        <!-- timeline -->
        <div class="eu-flex-item eu-basis_12 eu-basis_6_lg eu-pl_3">
          <TransactionTimeline
            :transactions="charge.transactions"
            @openDrawer="handleOpenDrawer"
          />
          <!-- Metadado -->
          <Metadata
            v-if="showMetadata"
            :metadata="charge.metadata"
          />
          <log-links
            :entity="charge"
            :title="$t('log-links.charge')"
          ></log-links>
        </div>
      </div>
      <last-log />
    </div>

    <modal-password
      v-if="attemptModal"
      @cancel="closeModal"
      :callback="attemptCharge"
    ></modal-password>

    <modal
      v-if="cancelModal"
      :open="cancelModal"
    >
      <template slot="content">
        <div
          class="emd-container _highlight_header _footer_end _footer_spaced_mobile"
        >
          <div class="emd-container__header">
            <h2 class="emd-text _size_section _color_secondary">
              {{ $t('charge.detail.modal.cancel.title') }}
            </h2>
            <button
              class="emd-btn _type_link jsModalClose"
              type="button"
              v-on:click="cancelModal = false"
            >
              <i class="uil uil-times" />
            </button>
          </div>
          <form
            @submit.prevent="cancelCharge"
            name="cancelCharge"
            novalidate="true"
          >
            <div class="emd-container__content">
              <emd-alert
                variant="danger"
                v-if="alert.error"
                @cancel="alert.error = false"
                :title="$t('charge.detail.modal.cancel.error')"
              >
                <template slot="content">
                  <p
                    v-for="(error, index) in errors"
                    :key="index"
                  >
                    {{ error.message }}
                  </p>
                </template>
              </emd-alert>
              <div class="emd-panel">
                <h2 class="emd-text _size_section">
                  {{ $t('charge.detail.modal.cancel.question') }}
                </h2>
              </div>
              <div class="emd-panel">
                <div class="eu-flex eu-column eu-row_md eu-mb_4">
                  <div class="eu-flex-item eu-basis_6_md">
                    <label
                      class="emd-radio-button"
                      for="cancelTotal"
                      data-children-count="1"
                    >
                      <input
                        class="emd-radio-button__input"
                        type="radio"
                        :value="true"
                        id="cancelTotal"
                        v-model="cancelTotal"
                      />
                      <div class="emd-radio-button__area"></div>
                      <span class="emd-radio-button__text">{{
                        $t('charge.detail.modal.cancel.total')
                      }}</span>
                    </label>
                  </div>
                  <div
                    class="eu-flex-item eu-basis_6_md"
                    v-if="canCancelPartial"
                  >
                    <label
                      class="emd-radio-button"
                      for="cancelParcial"
                      data-children-count="1"
                    >
                      <input
                        class="emd-radio-button__input"
                        type="radio"
                        :value="false"
                        id="cancelParcial"
                        v-model="cancelTotal"
                      />
                      <div class="emd-radio-button__area"></div>
                      <span class="emd-radio-button__text">{{
                        $t('charge.detail.modal.cancel.partial')
                      }}</span>
                    </label>
                  </div>
                </div>
                <div v-if="!cancelTotal && canCancelPartial">
                  <div>
                    <div class="eu-flex">
                      <div
                        v-if="recipients.length > 0"
                        class="eu-flex-item"
                      >
                        <emd-form-group
                          :label="$t('charge.detail.modal.cancel.recipient')"
                          required
                        >
                          <emd-select v-model="cancelRecipientId">
                            <option value="">
                              {{ $t('charge.detail.modal.cancel.select') }}
                            </option>
                            <option
                              :key="recipient.split.recipient_gateway_id"
                              v-for="recipient in recipients"
                              :value="recipient.split.recipient_gateway_id"
                            >
                              {{ recipient.name }}
                            </option>
                          </emd-select>
                        </emd-form-group>
                      </div>

                      <div class="eu-flex-item">
                        <emd-form-group
                          :label="$t('charge.detail.modal.cancel.amount')"
                          required
                        >
                          <div class="emd-input-group">
                            <money
                              class="emd-input"
                              type="text"
                              v-bind="money"
                              v-model="cancelAmount"
                            ></money>
                          </div>
                        </emd-form-group>
                      </div>
                    </div>
                  </div>
                </div>
                <password-confirmation
                  v-model="password"
                  :label="null"
                ></password-confirmation>
              </div>
            </div>
            <div class="emd-container__footer">
              <button
                class="emd-btn _color_primary _type_text"
                type="button"
                v-on:click="closeModal('cancelModal')"
                :disabled="cancelLoading"
              >
                {{ $t('charge.detail.modal.cancel.button-cancel') }}
              </button>
              <button
                class="emd-btn _color_primary"
                type="submit"
                :disabled="!password || cancelLoading"
              >
                <emd-loader
                  :type="''"
                  v-if="cancelLoading"
                ></emd-loader>
                <span v-else>{{
                  $t('charge.detail.modal.cancel.button-ok')
                }}</span>
              </button>
            </div>
          </form>
        </div>
      </template>
    </modal>
    <!--  Remove captureModal -->
    <modal
      v-if="captureModal"
      :open="openCaptureModal || captureModal"
    >
      <template slot="content">
        <div
          class="emd-container _highlight_header _footer_end _footer_spaced_mobile"
        >
          <div class="emd-container__header">
            <h2 class="emd-text _size_section _color_secondary">
              {{ $t('charge.detail.modal.capture.title') }}
            </h2>
            <button
              class="emd-btn _type_link jsModalClose"
              type="button"
              v-on:click="captureModal = false"
            >
              <i class="uil uil-times" />
            </button>
          </div>
          <form
            @submit.prevent="captureCharge"
            name="captureCharge"
            novalidate="true"
          >
            <div class="emd-container__content">
              <emd-alert
                variant="danger"
                v-if="alert.error"
                @cancel="alert.error = false"
                :title="$t('charge.detail.modal.capture.error')"
              >
                <template slot="content">
                  <p
                    v-for="(error, index) in errors"
                    :key="index"
                  >
                    {{ error.message }}
                  </p>
                </template>
              </emd-alert>
              <div class="emd-panel">
                <h2 class="emd-text _size_section">
                  {{ $t('charge.detail.modal.capture.question') }}
                </h2>
              </div>
              <div class="emd-panel">
                <div class="eu-flex eu-column eu-row_md eu-mb_4">
                  <div class="eu-flex-item eu-basis_6_md">
                    <div class="emd-form-group">
                      <label
                        class="emd-radio-button"
                        for="captureTotal"
                        data-children-count="1"
                      >
                        <input
                          class="emd-radio-button__input"
                          type="radio"
                          :value="true"
                          id="captureTotal"
                          v-model="captureTotal"
                        />
                        <div class="emd-radio-button__area"></div>
                        <span class="emd-radio-button__text">{{
                          $t('charge.detail.modal.capture.total')
                        }}</span>
                      </label>
                    </div>
                  </div>
                  <div class="eu-flex-item eu-basis_6_md">
                    <div class="emd-form-group">
                      <label
                        class="emd-radio-button"
                        for="captureParcial"
                        data-children-count="1"
                      >
                        <input
                          class="emd-radio-button__input"
                          type="radio"
                          :value="false"
                          id="captureParcial"
                          v-model="captureTotal"
                        />
                        <div class="emd-radio-button__area"></div>
                        <span class="emd-radio-button__text">{{
                          $t('charge.detail.modal.capture.partial')
                        }}</span>
                      </label>
                    </div>
                  </div>
                </div>
                <div v-if="!captureTotal">
                  <div>
                    <div class="emd-form-group">
                      <label
                        class="emd-label _required"
                        for="money-capture"
                        >{{ $t('charge.detail.modal.capture.amount') }}</label
                      >
                      <div class="emd-input-group">
                        <money
                          class="emd-input"
                          id="money-capture"
                          type="text"
                          v-bind="money"
                          v-model="captureAmount"
                        ></money>
                      </div>
                    </div>
                  </div>
                </div>
                <password-confirmation
                  v-model="password"
                  :label="null"
                ></password-confirmation>
              </div>
            </div>
            <div class="emd-container__footer">
              <button
                class="emd-btn _color_primary _type_text"
                type="button"
                v-on:click="closeModal('captureModal')"
                :disabled="captureLoading"
              >
                {{ $t('charge.detail.modal.capture.button-cancel') }}
              </button>
              <button
                class="emd-btn _color_primary"
                type="submit"
                :disabled="!password || captureLoading"
              >
                <emd-loader
                  :type="''"
                  v-if="captureLoading"
                ></emd-loader>
                <span v-else>{{
                  $t('charge.detail.modal.capture.button-ok')
                }}</span>
              </button>
            </div>
          </form>
        </div>
      </template>
    </modal>

    <modal
      v-if="confirmModal"
      :open="confirmModal || openConfirmModal"
    >
      <template slot="content">
        <div
          class="emd-container _highlight_header _footer_end _footer_spaced_mobile"
        >
          <div class="emd-container__header">
            <h2 class="emd-text _size_section _color_secondary">
              {{ $t('charge.detail.modal.confirm.title') }}
            </h2>
            <button
              class="emd-btn _type_link jsModalClose"
              type="button"
              v-on:click="confirmModal = false"
            >
              <i class="uil uil-times" />
            </button>
          </div>
          <form
            @submit.prevent="confirmCharge"
            name="confirmCharge"
            novalidate="true"
          >
            <div class="emd-container__content">
              <emd-alert
                variant="danger"
                v-if="alert.error"
                @cancel="alert.error = false"
                :title="$t('charge.detail.modal.confirm.error')"
              >
                <template slot="content">
                  <p
                    v-for="(error, index) in errors"
                    :key="index"
                  >
                    {{ error.message }}
                  </p>
                </template>
              </emd-alert>
              <div class="emd-panel">
                <div class="emd-panel__section">
                  <div class="emd-panel__block">
                    <div class="emd-form-group">
                      <label
                        class="emd-label _required"
                        for="money-confirm"
                      >
                        {{ $t('charge.detail.modal.confirm.amount') }}
                      </label>
                      <div class="emd-input-group">
                        <money
                          id="money-confirm"
                          class="emd-input"
                          type="text"
                          v-bind="money"
                          v-model="confirmAmount"
                        ></money>
                      </div>
                    </div>
                  </div>
                </div>
                <password-confirmation
                  v-model="password"
                ></password-confirmation>
              </div>
            </div>
            <div class="emd-container__footer">
              <button
                class="emd-btn _color_primary _type_text"
                type="button"
                v-on:click="closeModal('confirmModal')"
                :disabled="confirmLoading"
              >
                {{ $t('charge.detail.modal.confirm.button-cancel') }}
              </button>
              <button
                class="emd-btn _color_primary"
                type="submit"
                :disabled="!password || confirmLoading"
              >
                <emd-loader
                  :type="''"
                  v-if="confirmLoading"
                ></emd-loader>
                <span v-else>{{
                  $t('charge.detail.modal.confirm.button-ok')
                }}</span>
              </button>
            </div>
          </form>
        </div>
      </template>
    </modal>

    <modal
      v-if="letterModal"
      :open="letterModal"
    >
      <template slot="content">
        <div class="emd-container _highlight_header _footer_end">
          <div class="emd-container__header">
            <h2 class="emd-text _size_section _color_secondary">
              {{ this.$t(`charge.detail.modal.letter.${letterType}-letter`) }}
            </h2>
            <button
              class="emd-btn _type_link jsModalClose"
              type="button"
              @click="closeModal('letterModal')"
            >
              <i class="uil uil-times" />
            </button>
          </div>
          <div class="emd-container__content">
            <emd-alert
              variant="warning"
              v-if="showLetterAlert && !alert.error"
              :autoclose="false"
              :title="
                $t(`charge.detail.modal.letter.${letterType}-warning-title`)
              "
              @cancel="showLetterAlert = false"
            >
              <template slot="content">
                {{ $t('charge.detail.modal.letter.warning-text') }}
              </template>
            </emd-alert>
            <div
              class="eu-align_center eu-pa_3"
              v-if="!alert.error"
            >
              <emd-loader :type="'_invert'"></emd-loader>
            </div>

            <emd-alert
              variant="danger"
              v-if="alert.error"
              @cancel="alert.error = false"
              :title="
                $t(`charge.detail.modal.letter.${letterType}-error-title`)
              "
            >
              <template slot="content">
                <p
                  v-for="(error, index) in errors"
                  :key="index"
                >
                  {{ error.message }}
                </p>
              </template>
            </emd-alert>
          </div>
        </div>
      </template>
    </modal>

    <modal-charge-reprocess
      v-if="chargeReady && canRetryCharge"
      :charge="charge"
      :isOpen="retryModal"
      :error="retryError"
      :isLoad="retryLoading"
      :antifraudEnabled="hasRetryWithAntifraud"
      @submit="retry"
      @close="closeModal('retryModal')"
    />

    <boleto-cancel-modal
      v-if="showBoletoCancelModal"
      :open="boletoCancelModal"
      @close="closeBoletoCancelModal"
    >
      <boleto-cancel
        :totalAmount="getAvailableAmountToBoletoCancel"
        :split="getSplits"
        :chargeId="charge.id"
        @close="closeBoletoCancelModal"
        @success="refreshPage"
      />
    </boleto-cancel-modal>

    <simulate-payments-modal
      v-if="showSimulatorPaymentsModal"
      :open="showSimulatorPaymentsModal"
      :name="charge.customer.name"
      :email="charge.customer.email"
      :value="charge.amount"
      :transactionId="charge?.pgid"
      @handleClose="closeSimulatorPaymentsModal"
    >
    </simulate-payments-modal>

    <TransactionDetails
      v-if="drawer.open"
      :open.sync="drawer.open"
      :data="drawer.data"
      :entity="charge"
    >
      <template #log>
        <log-links
          v-if="showLogLinks"
          :title="$t('log-links.transaction')"
          :isSection="false"
          :entity="charge"
          noPadding
        />
      </template>
    </TransactionDetails>
  </div>
</template>

<script type="text/javascript">
import '@pagarme/sales-charges/web-comp'
import {
  EmdAlert,
  EmdFormGroup,
  EmdLoader,
  EmdSelect
} from 'emerald-vue/src/components'
import { Money } from 'v-money'
import { mapGetters } from 'vuex'

import { chargesEvents as Amplitude } from '@/amplitude-events/sales.amplitude'
import { isPsP } from '@/enums/business-model.enum.js'
import { isBoleto, isPix } from '@/enums/payment-method.enum'
import { isPercentage } from '@/enums/percentage.enum'
import { formatErrorMessage } from '@/support/formatErrorMessage'
import { chargeLogEnabled } from '@permissions/log.permissions'
import MathManager from '@utils/math-manager.utils'
import { isSalesNewLayoutFlagEnabled } from '../../../feature-flags/sales.feature-flags'

export default {
  name: 'ChargesDetail',
  components: {
    Money,
    EmdAlert,
    EmdLoader,
    EmdFormGroup,
    EmdSelect,
    Modal: () => import('@/components/project/globals/Modal.vue'),
    ModalPassword: () =>
      import('@/components/project/globals/ModalPassword.vue'),
    PasswordConfirmation: () =>
      import('@/components/project/molecules/PasswordConfirmation.vue'),
    LogLinks: () => import('@/components/project/molecules/LogLinks/index.vue'),
    LastLog: () => import('@/components/project/molecules/LastLog.vue'),
    ModalChargeReprocess: () =>
      import('@/components/project/globals/ModalChargeReprocess.vue'),
    PayerDetails: () => import('./PayerDetails'),
    PaymentMethod: () => import('./PaymentMethod'),
    Recipients: () => import('./Recipients'),
    BoletoCancel: () => import('./Recipients/BoletoCancel'),
    BoletoCancelModal: () =>
      import('./Recipients/BoletoCancel/BoletoCancelModal'),
    AlertReasonRefusal: () =>
      import('./AlertReasonRefusal/AlertReasonRefusal.vue'),
    TransactionSummary: () => import('./TransactionSummary/index.vue'),
    ChargeDetailTable: () => import('./ChargeDetailTable/ChargeDetailTable'),
    Metadata: () => import('./Metadata/Metadata.vue'),
    TransactionTimeline: () => import('./TransactionTimeline/index.vue'),
    TransactionDetails: () => import('./TransactionDetails/index.vue'),
    SimulatePaymentsModal: () => import('./SimulatePaymentsModal/index.vue')
  },
  data () {
    return {
      isSalesNewLayoutFlagEnabled: false,
      drawer: {
        open: false,
        data: {}
      },
      transactions: [],
      alertSuccess: {
        isOpen: false,
        title: null,
        message: null
      },
      charge: {
        status: '',
        cancel: {
          show: false
        },
        capture: {
          show: false
        },
        confirm: {
          show: false
        },
        customer: {
          phones: {}
        },
        last_transaction: {
          amount: '',
          card: {}
        },
        boleto_cancel: {
          show: false
        },
        boleto_cancellation_letter: {
          show: false
        },
        transactions: []
      },
      recipients: [],
      splits: [],
      loadingDetail: true,
      cancelTotal: true,
      captureTotal: true,
      cancelModal: false,
      captureModal: false,
      confirmModal: false,
      retryModal: false,
      cancelLoading: false,
      captureLoading: false,
      confirmLoading: false,
      retryLoading: false,
      boletoCancelModal: false,
      cancelSplit: [],
      cancelRecipientId: '',
      cancelAmount: undefined,
      captureAmount: undefined,
      confirmAmount: undefined,
      canCancelPartial: true,
      password: '',
      errors: [],
      retryError: null,
      money: {
        decimal: ',',
        thousands: '.',
        prefix: 'R$ ',
        suffix: '',
        precision: 2,
        masked: true
      },
      alert: {
        success: false,
        error: false,
        type: 'capture'
      },
      attemptModal: false,
      showAttemptButton: false,
      letterModal: false,
      letterType: 'authorization',
      showLetterAlert: true,
      isLoadingTransactions: true,
      splitAlert: true,
      chargeReady: false,
      showSimulatorPaymentsModal: false
    }
  },
  computed: {
    ...mapGetters({
      getCharge: 'charge',
      permissionIsEnabled: 'permissionIsEnabled',
      merchantId: 'merchantId',
      accountId: 'accountId',
      pagarmeCompanyId: 'pagarmeCompanyId'
    }),
    chargesNewLayoutEnabled () {
      return true
    },
    params () {
      return {
        accountId: this.accountId,
        merchantId: this.merchantId,
        chargeId: this.$route.params.id
      }
    },
    showLogLinks () {
      return chargeLogEnabled(this.permissionIsEnabled)
    },
    showBoletoCancelModal () {
      return this.isBoleto && this.boletoCancelModal === true
    },
    showPayableInstallment () {
      const payable = this.charge?.payable
      if (payable) {
        return payable.show_payable_installment
      }

      return this.getSplits && this.getSplits.length > 0
    },
    showMetadata () {
      return this.charge.metadata && Object.entries(this.charge.metadata).length
    },
    isCancelableBoletoSuccess () {
      return (
        this.isBoleto &&
        ['processing', 'processando'].includes(this.charge.status.toLowerCase())
      )
    },
    isCancelableBoleto () {
      return this.isBoleto && this.charge?.boleto_cancel?.show === true
    },
    isBoleto () {
      return isBoleto(this.charge?.payment_method)
    },
    isPSP () {
      return isPsP(this.$store.getters.businessModel)
    },
    getConsolidated () {
      return this.charge?.payable?.consolidated
    },
    getSplits () {
      return this.charge?.last_transaction?.splits
    },
    getAvailableAmountToBoletoCancel () {
      return this.charge?.boleto_cancel?.value
    },
    metadata () {
      if (this.charge.metadata) {
        if (Object.hasOwn(this.charge, 'metadata')) {
          return this.charge.metadata
        } else {
          return false
        }
      } else {
        return false
      }
    },
    canRetryCharge () {
      return (
        this.charge.retry_with_antifraud.show ||
        this.charge.retry_without_antifraud.show
      )
    },
    hasPayerPix () {
      return (
        isPix(this.charge.payment_method) &&
        this.charge?.last_transaction?.payer
      )
    }
  },
  created () {
    this.getChargeDetail()
    this.$store.dispatch('GET_LAST_LOG', this.params)
  },
  async mounted () {
    this.$nextTick(() =>
      Amplitude.events.view.loadDetailsView(this.$route.query)
    )

    this.isSalesNewLayoutFlagEnabled = await isSalesNewLayoutFlagEnabled()

    if (!this.$route.params.id.includes('ch_')) {
      this.$router.push({ name: 'charges' }).catch(err => err)
    }
    document.addEventListener(
      'mouseup',
      e => {
        const actionsDropdown = document.getElementById('jsDropdown')
        if (actionsDropdown && !actionsDropdown.contains(e.target)) {
          this.$refs.chargeDetailTable.closeActionsDropdown()
        }
      },
      { passive: true }
    )
  },
  methods: {
    openModalSimulatorPayments () {
      this.showSimulatorPaymentsModal = true
    },
    closeSimulatorPaymentsModal () {
      this.showSimulatorPaymentsModal = false
    },
    openBoletoCancelModal () {
      this.boletoCancelModal = true
    },
    openRetryModal () {
      this.retryModal = true
      this.retryError = null
    },
    openModalRefund () {
      if (this.isCancelableBoleto) {
        return this.openBoletoCancelModal()
      }
      this.cancelModal = true
    },
    openCaptureModal () {
      this.captureModal = true
    },
    openConfirmModal () {
      this.confirmModal = true
    },
    openAttemptModal () {
      this.attemptModal = true
    },
    closeBoletoCancelModal () {
      this.boletoCancelModal = false
    },
    closeModal (modal) {
      this[modal] = false
      this.password = ''
      this.alert.success = false
      this.alert.error = false
      this.cancelLoading = false
      this.captureLoading = false
      this.confirmLoading = false
      this.attemptModal = false
      this.showLetterAlert = true
    },
    handleOpenDrawer (transaction) {
      this.drawer.open = true
      this.drawer.data = { transaction, orderKey: this.charge?.pgid }
    },
    isTrue: str => ['yes', 'sim'].includes(str?.toLowerCase()),
    formatCurrency (value) {
      return MathManager.formatCurrencyLegacy(value)
    },
    refreshPage () {
      window.location.reload()
    },
    handleSuccess (response) {
      this.password = ''
      this.errors = []
      this.params.refresh = true
      if (response) {
        this.prepareChargeDetail(response)
      } else {
        this.getChargeDetail()
      }
      this.$store.dispatch('GET_LAST_LOG', this.params)
    },
    handleError (error) {
      this.errors = error && error.body ? error.body.errors : []
      this.alert.error = true
      this.alert.success = false
      setTimeout(() => {
        this.alert.error = false
      }, 7000)
    },
    formatAmountToNumber (amount, type) {
      return isPercentage(type)
        ? MathManager.percentageToNumber(amount) * 100
        : MathManager.parseCurrencyToNumber(amount)
    },
    cancelCharge () {
      const amount = this.cancelTotal
        ? this.charge.cancel.value
        : Number(this.cancelAmount.replace(/\D+/g, ''))

      const body = {
        amount,
        password: window.btoa(this.password)
      }

      if (amount > this.charge.cancel.value) {
        const error = {
          body: {
            errors: [
              { message: this.$t('charge.detail.modal.cancel.amount-validate') }
            ]
          }
        }
        this.handleError(error)
        return
      }

      if (this.cancelRecipientId) {
        body.split = this.recipients.map(({ split }, index) => ({
          gateway_id: split.recipient_gateway_id,
          recipient_id: split.recipient_id,
          type:
            this.cancelRecipientId === split.recipient_gateway_id
              ? 'flat'
              : split.type,
          amount:
            this.cancelRecipientId === split.recipient_gateway_id ? amount : 0,
          split_options: {
            charge_processing_fee: this.isTrue(
              split?.options?.charge_processing_fee
            ),
            charge_remainder_fee: this.isTrue(
              split?.options?.charge_remainder_fee
            ),
            liable: this.isTrue(split?.options?.liable)
          }
        }))
      }
      this.cancelLoading = true
      this.$store
        .dispatch('CANCEL_CHARGE', { ...this.params, body })
        .then(res => {
          this.cancelModal = false
          this.cancelLoading = false
          this.alertSuccess = {
            isOpen: true,
            title: this.$t('charge.detail.modal.cancel.success-title'),
            message: this.$t('charge.detail.modal.cancel.success-content')
          }
          this.handleSuccess(res)
        })
        .catch(err => {
          this.cancelLoading = false
          this.handleError(err)
        })
    },
    captureCharge () {
      const body = {
        password: window.btoa(this.password)
      }
      if (this.captureTotal) {
        body.amount = this.charge.capture.value
      } else {
        body.amount = this.captureAmount.replace(/\D+/g, '')
      }
      this.captureLoading = true
      this.$store
        .dispatch('CAPTURE_CHARGE', { ...this.params, body })
        .then(res => {
          this.captureModal = false
          this.captureLoading = false
          this.handleSuccess(res)
        })
        .catch(err => {
          this.captureLoading = false
          this.handleError(err)
        })
    },
    confirmCharge () {
      const body = {
        amount: this.confirmAmount.replace(/\D+/g, ''),
        password: window.btoa(this.password)
      }
      this.confirmLoading = true
      this.$store
        .dispatch('CAPTURE_CHARGE', { ...this.params, body })
        .then(res => {
          this.confirmModal = false
          this.confirmLoading = false
          this.handleSuccess(res)
        })
        .catch(err => {
          this.confirmLoading = false
          this.handleError(err)
        })
    },
    getChargeDetail () {
      this.loadingDetail = true
      this.charge.transactions = []
      this.params.query = {
        is_test_account:
          this.$store.getters.account.account_type.value === 'test'
      }
      if (this.$route.query.created_at) {
        this.params.query.created_at = this.$route.query.created_at
      }
      this.prepareChargeDetail(this.getCharge)
    },
    prepareChargeDetail (charge) {
      this.charge = Object.assign(this.charge, charge)

      this.chargeReady = true
      if (this.charge.transactions.length)
        this.charge.transactions[0].isOpen = true

      this.recipients = this.charge.last_transaction.splits.map(split => ({
        ...split.recipient,
        consolidated: this.charge?.payable?.consolidated?.find(
          ({ recipient_id: recipientId }) => recipientId === split.recipient.id
        ),
        split: {
          type: split.type === 'Flat' ? 'flat' : 'percentage',
          recipient_id: split.recipient.id,
          recipient_gateway_id: split.gateway_id,
          amount: this.formatAmountToNumber(split.amount, split.type),
          anticipation_fee:
            split.anticipation_fee &&
            MathManager.parseCurrencyToNumber(split.anticipation_fee),
          fee: split.fee && MathManager.parseCurrencyToNumber(split.fee),
          options: split.options
        }
      }))

      this.cancelAmount = charge.cancel.value / 100
      this.captureAmount = charge.capture.value / 100
      this.confirmAmount = charge.confirm.value / 100

      const pixCanCancelPartial =
        charge.payment_method === 'Pix' && charge.cancel.show
      const hasStatusToCancelPartial =
        charge.status !== 'Authorized pending capture' ||
        charge.status !== 'Autorizado captura pendente'
      const hasPaymentMethodToCancelPartial =
        charge.payment_method === 'Cartão de crédito' ||
        charge.payment_method === 'Credit card' ||
        charge.payment_method === 'credit_card' ||
        charge.payment_method === 'Private Label' ||
        charge.last_transaction.acquirer === 'Ticket'

      this.canCancelPartial =
        (hasStatusToCancelPartial && hasPaymentMethodToCancelPartial) ||
        pixCanCancelPartial

      this.showAttemptButton =
        (charge.status === 'Failed' || charge.status === 'Falha') &&
        (charge.payment_method === 'Cartão de crédito' ||
          charge.payment_method === 'Credit card' ||
          charge.payment_method === 'Private Label') &&
        this.componentIsEnabled('charge', 'attempt') &&
        this.permissionIsEnabled('charge:attempt')
      this.loadingDetail = false
    },
    async attemptCharge (password) {
      this.params.body = { password: password }
      try {
        const res = await this.$store.dispatch('ATTEMPT_CHARGE', this.params)
        this.attemptModal = false
        this.handleSuccess(res)
        return res
      } catch (err) {
        return await Promise.reject(err)
      }
    },
    getLetter (type) {
      this.$refs.chargeDetailTable.closeActionsDropdown()
      this.letterType = type.toLowerCase()
      this.letterModal = true
      this.params.query.type = type
      this.$store
        .dispatch('GET_LETTER', this.params)
        .then(res => {
          var link = document.createElement('a')
          link.href = res.url
          link.download = res.url
          link.target = '_blank'
          link.id = 'letterDownload'
          link.rel = 'noopener noreferrer'
          this.closeModal('letterModal')
          document.body.appendChild(link)
          document.getElementById('letterDownload').click()
          document.getElementById('letterDownload').remove()
        })
        .catch(err => {
          this.handleError(err)
          setTimeout(() => {
            this.closeModal('letterModal')
          }, 7000)
        })
    },
    resync () {
      this.$refs.chargeDetailTable.closeActionsDropdown()
      this.loadingDetail = true
      this.$store
        .dispatch('RESYNC_CHARGE', this.params)
        .then(res => {
          this.loadingDetail = false
          this.handleSuccess(res)
        })
        .catch(err => {
          this.loadingDetail = false
          this.handleError(err)
        })
    },
    retry (body) {
      this.retryLoading = true
      this.$store
        .dispatch('RETRY_CHARGE', { ...this.params, body })
        .then(res => {
          this.handleSuccess(res)
          this.alertSuccess = {
            isOpen: true,
            title: this.$t('charge.detail.modal.retry.success-title'),
            message: this.$t('charge.detail.modal.retry.success-content')
          }
          this.closeModal('retryModal')
        })
        .catch(error => {
          const errorMessage = error.body.notifications

          this.retryError = errorMessage
            ? errorMessage[Object.keys(errorMessage)[0]]?.join([' '])
            : formatErrorMessage(
                error,
                this.$t('modal.password.error-default')
              )?.join([' '])
        })
        .finally(() => {
          this.retryLoading = false
        })
    },
    componentIsEnabled (section, component) {
      return this.$store.getters.componentIsEnabled(section, component)
    }
  }
}
</script>

<style>
.emd-container {
  overflow: inherit;
}
.emd-nav-list__item .emd-btn._type_link:hover {
  text-decoration: none;
}
.emd-nav-list__item {
  margin-right: 0;
}

.underline {
  text-decoration: underline;
}
</style>
